import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api, isApiConfigured } from '@/lib/api'
import { getCurrentUserId } from '@/lib/sync'
import { getAffectionLevel } from '@/lib/affection'
import { realtimeClient } from '@/lib/websocket'
import type {
  Partnership,
  PartnershipWithDetails,
  InviteCode,
  PartnerQuest,
  PartnerQuestWithDetails,
  WaifuMode,
  QuestStatus,
  PartnershipStatus,
} from '@/types/social'

interface PartnershipState {
  waifuMode: WaifuMode
  partnerships: Partnership[]
  inviteCodes: InviteCode[]
  partnerQuests: PartnerQuest[]
  isLoading: boolean
  error: string | null

  setWaifuMode: (mode: WaifuMode) => void
  generateInviteCode: (suggestedNickname?: string) => Promise<InviteCode | null>
  redeemInviteCode: (code: string, myNicknameForThem?: string) => Promise<Partnership | null>
  getMyInviteCodes: () => InviteCode[]
  fetchPartnerships: () => Promise<void>
  getPartnershipById: (id: string) => Partnership | undefined
  getActivePartnerships: () => Partnership[]
  getPartnershipWithDetails: (id: string) => Promise<PartnershipWithDetails | null>
  getAllPartnershipsWithDetails: () => Promise<PartnershipWithDetails[]>
  updateNickname: (partnershipId: string, nickname: string) => Promise<void>
  toggleSpicyMode: (partnershipId: string, enabled: boolean) => Promise<void>
  addAffection: (partnershipId: string, amount: number) => Promise<{ newLevel: number; oldLevel: number; leveledUp: boolean } | null>
  breakUp: (partnershipId: string) => Promise<void>
  repairPartnership: (partnershipId: string) => Promise<void>
  fetchPartnerQuests: (partnershipId?: string) => Promise<void>
  createPartnerQuest: (quest: Omit<PartnerQuest, 'id' | 'status' | 'acceptedAt' | 'completedAt' | 'verifiedAt' | 'proofMediaId' | 'createdAt' | 'updatedAt'>) => Promise<PartnerQuest | null>
  updateQuestStatus: (questId: string, status: QuestStatus) => Promise<void>
  completeQuestWithProof: (questId: string, proofFile: File) => Promise<{ success: boolean; mediaUrl?: string; error?: string }>
  getQuestProofUrl: (questId: string) => Promise<string | null>
  getQuestsAssignedToMe: () => PartnerQuest[]
  getQuestsAssignedByMe: () => PartnerQuest[]
  getQuestWithDetails: (questId: string) => Promise<PartnerQuestWithDetails | null>
  subscribeToUpdates: () => () => void
  reset: () => void
}

function getPartnerInfo(partnership: Partnership, currentUserId: string) {
  const isUserA = partnership.userAId === currentUserId
  return {
    partnerId: isUserA ? partnership.userBId : partnership.userAId,
    myNicknameForThem: isUserA ? partnership.nicknameAForB : partnership.nicknameBForA,
    theirNicknameForMe: isUserA ? partnership.nicknameBForA : partnership.nicknameAForB,
    mySpicyEnabled: isUserA ? partnership.spicyEnabledA : partnership.spicyEnabledB,
    theirSpicyEnabled: isUserA ? partnership.spicyEnabledB : partnership.spicyEnabledA,
    myAffection: isUserA ? partnership.affectionA : partnership.affectionB,
    theirAffection: isUserA ? partnership.affectionB : partnership.affectionA,
  }
}

// Helper to map API row to Partnership
function mapPartnership(p: Record<string, unknown>): Partnership {
  return {
    id: p.id as string,
    userAId: (p.user_a_id ?? p.userAId) as string,
    userBId: (p.user_b_id ?? p.userBId) as string,
    nicknameAForB: (p.nickname_a_for_b ?? p.nicknameAForB ?? null) as string | null,
    nicknameBForA: (p.nickname_b_for_a ?? p.nicknameBForA ?? null) as string | null,
    spicyEnabledA: Boolean(p.spicy_enabled_a ?? p.spicyEnabledA),
    spicyEnabledB: Boolean(p.spicy_enabled_b ?? p.spicyEnabledB),
    affectionA: (p.affection_a ?? p.affectionA ?? 0) as number,
    affectionB: (p.affection_b ?? p.affectionB ?? 0) as number,
    status: ((p.status as string) || 'active') as PartnershipStatus,
    brokenUpBy: (p.broken_up_by ?? p.brokenUpBy ?? null) as string | null,
    brokenUpAt: (p.broken_up_at ?? p.brokenUpAt ?? null) as string | null,
    createdAt: (p.created_at ?? p.createdAt) as string,
    updatedAt: (p.updated_at ?? p.updatedAt) as string,
  }
}

function mapQuest(q: Record<string, unknown>): PartnerQuest {
  return {
    id: q.id as string,
    partnershipId: (q.partnership_id ?? q.partnershipId) as string,
    assignedBy: (q.assigned_by ?? q.assignedBy) as string,
    assignedTo: (q.assigned_to ?? q.assignedTo) as string,
    title: q.title as string,
    description: (q.description ?? null) as string | null,
    emoji: (q.emoji ?? '⭐') as string,
    xpReward: (q.xp_reward ?? q.xpReward ?? 25) as number,
    affectionReward: (q.affection_reward ?? q.affectionReward ?? 10) as number,
    customReward: (q.custom_reward ?? q.customReward ?? null) as string | null,
    stake: (q.stake ?? null) as string | null,
    dueAt: (q.due_at ?? q.dueAt ?? null) as string | null,
    status: (q.status ?? 'pending') as QuestStatus,
    acceptedAt: (q.accepted_at ?? q.acceptedAt ?? null) as string | null,
    completedAt: (q.completed_at ?? q.completedAt ?? null) as string | null,
    verifiedAt: (q.verified_at ?? q.verifiedAt ?? null) as string | null,
    proofMediaId: (q.proof_media_id ?? q.proofMediaId ?? null) as string | null,
    createdAt: (q.created_at ?? q.createdAt) as string,
    updatedAt: (q.updated_at ?? q.updatedAt) as string,
  }
}

export const usePartnershipStore = create<PartnershipState>()(
  persist(
    (set, get) => ({
      waifuMode: 'solo',
      partnerships: [],
      inviteCodes: [],
      partnerQuests: [],
      isLoading: false,
      error: null,

      setWaifuMode: (mode) => {
        set({ waifuMode: mode })
        if (isApiConfigured()) {
          const userId = getCurrentUserId()
          if (userId !== 'local') {
            api.patch('/api/profile', { waifu_mode: mode }).catch(console.error)
          }
        }
      },

      generateInviteCode: async (suggestedNickname) => {
        if (!isApiConfigured()) { set({ error: 'API not configured' }); return null }
        const userId = getCurrentUserId()
        if (userId === 'local') { set({ error: 'Must be logged in' }); return null }

        set({ isLoading: true, error: null })
        try {
          const data = await api.post<{ id: string; code: string; expires_at: string }>(
            '/api/invite-codes',
            { suggested_nickname: suggestedNickname }
          )

          const inviteCode: InviteCode = {
            id: data.id,
            code: data.code,
            creatorId: userId,
            suggestedNickname: suggestedNickname ?? null,
            usedById: null,
            usedAt: null,
            partnershipId: null,
            expiresAt: data.expires_at,
            createdAt: new Date().toISOString(),
          }

          set((state) => ({
            inviteCodes: [...state.inviteCodes, inviteCode],
            isLoading: false,
          }))
          return inviteCode
        } catch (error) {
          console.error('[Partnership] Failed to generate invite code:', error)
          set({ error: 'Failed to generate invite code', isLoading: false })
          return null
        }
      },

      redeemInviteCode: async (code, _myNicknameForThem) => {
        if (!isApiConfigured()) { set({ error: 'API not configured' }); return null }
        const userId = getCurrentUserId()
        if (userId === 'local') { set({ error: 'Must be logged in' }); return null }

        set({ isLoading: true, error: null })
        try {
          const data = await api.post<{ partnership_id: string }>(
            '/api/invite-codes/redeem',
            { code }
          )

          // Fetch the full partnership
          await get().fetchPartnerships()
          const partnership = get().partnerships.find(p => p.id === data.partnership_id)

          set({ waifuMode: 'social', isLoading: false })
          return partnership ?? null
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to redeem invite code'
          console.error('[Partnership] Failed to redeem:', error)
          set({ error: message, isLoading: false })
          return null
        }
      },

      getMyInviteCodes: () => {
        const userId = getCurrentUserId()
        return get().inviteCodes.filter((code) => code.creatorId === userId)
      },

      fetchPartnerships: async () => {
        if (!isApiConfigured()) return
        const userId = getCurrentUserId()
        if (userId === 'local') return

        set({ isLoading: true })
        try {
          const data = await api.get<Record<string, unknown>[]>('/api/partnerships')
          set({ partnerships: data.map(mapPartnership), isLoading: false })
        } catch (error) {
          console.error('[Partnership] Failed to fetch:', error)
          set({ isLoading: false })
        }
      },

      getPartnershipById: (id) => get().partnerships.find((p) => p.id === id),
      getActivePartnerships: () => get().partnerships.filter((p) => p.status === 'active'),

      getPartnershipWithDetails: async (id) => {
        if (!isApiConfigured()) return null
        const partnership = get().partnerships.find((p) => p.id === id)
        if (!partnership) return null

        const userId = getCurrentUserId()
        const { partnerId, myNicknameForThem, theirNicknameForMe, myAffection, theirAffection } = getPartnerInfo(partnership, userId)

        // Partner profile details come from the enriched partnerships API response
        const p = partnership as Partnership & {
          user_a_username?: string; user_a_display_name?: string; user_a_avatar_url?: string;
          user_b_username?: string; user_b_display_name?: string; user_b_avatar_url?: string;
        }
        const isUserA = partnership.userAId === userId
        const partnerUsername = isUserA ? (p as any).user_b_username : (p as any).user_a_username
        const partnerDisplayName = isUserA ? (p as any).user_b_display_name : (p as any).user_a_display_name
        const partnerAvatarUrl = isUserA ? (p as any).user_b_avatar_url : (p as any).user_a_avatar_url

        const affectionInfo = getAffectionLevel(myAffection)

        return {
          ...partnership,
          partnerId,
          partnerUsername: partnerUsername || null,
          partnerDisplayName: partnerDisplayName || null,
          partnerAvatarUrl: partnerAvatarUrl || null,
          partnerLastSeen: null,
          myNicknameForThem,
          theirNicknameForMe,
          isSpicyActive: partnership.spicyEnabledA && partnership.spicyEnabledB,
          isOnline: false,
          unreadEmotes: 0,
          pendingQuests: 0,
          myAffection,
          theirAffection,
          affectionLevel: affectionInfo.level,
          affectionTitle: affectionInfo.title,
        }
      },

      getAllPartnershipsWithDetails: async () => {
        const partnerships = get().getActivePartnerships()
        const details = await Promise.all(
          partnerships.map((p) => get().getPartnershipWithDetails(p.id))
        )
        return details.filter((d): d is PartnershipWithDetails => d !== null)
      },

      updateNickname: async (partnershipId, nickname) => {
        if (!isApiConfigured()) return
        try {
          await api.patch(`/api/partnerships/${partnershipId}`, { nickname })
          const userId = getCurrentUserId()
          const partnership = get().partnerships.find((p) => p.id === partnershipId)
          if (!partnership) return
          const isUserA = partnership.userAId === userId

          set((state) => ({
            partnerships: state.partnerships.map((p) =>
              p.id === partnershipId
                ? {
                    ...p,
                    nicknameAForB: isUserA ? nickname : p.nicknameAForB,
                    nicknameBForA: !isUserA ? nickname : p.nicknameBForA,
                  }
                : p
            ),
          }))
        } catch (error) {
          console.error('[Partnership] Failed to update nickname:', error)
        }
      },

      toggleSpicyMode: async (partnershipId, enabled) => {
        if (!isApiConfigured()) return
        try {
          await api.patch(`/api/partnerships/${partnershipId}`, { spicy_enabled: enabled })
          const userId = getCurrentUserId()
          const partnership = get().partnerships.find((p) => p.id === partnershipId)
          if (!partnership) return
          const isUserA = partnership.userAId === userId

          set((state) => ({
            partnerships: state.partnerships.map((p) =>
              p.id === partnershipId
                ? {
                    ...p,
                    spicyEnabledA: isUserA ? enabled : p.spicyEnabledA,
                    spicyEnabledB: !isUserA ? enabled : p.spicyEnabledB,
                  }
                : p
            ),
          }))
        } catch (error) {
          console.error('[Partnership] Failed to toggle spicy:', error)
        }
      },

      addAffection: async (partnershipId, amount) => {
        if (!isApiConfigured()) return null
        const userId = getCurrentUserId()
        const partnership = get().partnerships.find((p) => p.id === partnershipId)
        if (!partnership) return null

        const isUserA = partnership.userAId === userId
        const currentAffection = isUserA ? partnership.affectionA : partnership.affectionB
        const newAffection = currentAffection + amount

        const oldLevel = getAffectionLevel(currentAffection).level
        const newLevel = getAffectionLevel(newAffection).level
        const leveledUp = newLevel > oldLevel

        // Update locally immediately
        set((state) => ({
          partnerships: state.partnerships.map((p) =>
            p.id === partnershipId
              ? {
                  ...p,
                  affectionA: isUserA ? newAffection : p.affectionA,
                  affectionB: !isUserA ? newAffection : p.affectionB,
                }
              : p
          ),
        }))

        // Sync to server (affection is tracked via partnership update)
        // Note: In a full implementation, you'd have an affection field on partnerships
        // For now this stays local, same as before

        return { newLevel, oldLevel, leveledUp }
      },

      breakUp: async (partnershipId) => {
        if (!isApiConfigured()) return
        try {
          await api.patch(`/api/partnerships/${partnershipId}`, { status: 'broken_up' })
          const userId = getCurrentUserId()
          set((state) => ({
            partnerships: state.partnerships.map((p) =>
              p.id === partnershipId
                ? { ...p, status: 'broken_up' as const, brokenUpBy: userId, brokenUpAt: new Date().toISOString() }
                : p
            ),
          }))
        } catch (error) {
          console.error('[Partnership] Failed to break up:', error)
        }
      },

      repairPartnership: async (partnershipId) => {
        if (!isApiConfigured()) return
        try {
          await api.patch(`/api/partnerships/${partnershipId}`, { status: 'active' })
          set((state) => ({
            partnerships: state.partnerships.map((p) =>
              p.id === partnershipId
                ? { ...p, status: 'active' as const, brokenUpBy: null, brokenUpAt: null }
                : p
            ),
          }))
        } catch (error) {
          console.error('[Partnership] Failed to repair:', error)
        }
      },

      fetchPartnerQuests: async (_partnershipId) => {
        if (!isApiConfigured()) return
        const userId = getCurrentUserId()
        if (userId === 'local') return

        try {
          const data = await api.get<Record<string, unknown>[]>('/api/quests')
          set({ partnerQuests: data.map(mapQuest) })
        } catch (error) {
          console.error('[Partnership] Failed to fetch quests:', error)
        }
      },

      createPartnerQuest: async (questData) => {
        if (!isApiConfigured()) { set({ error: 'API not configured' }); return null }
        set({ isLoading: true, error: null })

        try {
          const data = await api.post<{ id: string }>('/api/quests', {
            partnership_id: questData.partnershipId,
            assigned_to: questData.assignedTo,
            title: questData.title,
            description: questData.description || null,
            emoji: questData.emoji || '⭐',
            xp_reward: questData.xpReward || 25,
            affection_reward: questData.affectionReward || 10,
            custom_reward: questData.customReward || null,
            stake: questData.stake || null,
            due_at: questData.dueAt || null,
          })

          const quest: PartnerQuest = {
            id: data.id,
            ...questData,
            status: 'pending',
            acceptedAt: null,
            completedAt: null,
            verifiedAt: null,
            proofMediaId: null,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }

          set((state) => ({ partnerQuests: [...state.partnerQuests, quest], isLoading: false }))
          return quest
        } catch (error) {
          console.error('[Partnership] Failed to create quest:', error)
          set({ error: 'Failed to create quest', isLoading: false })
          return null
        }
      },

      updateQuestStatus: async (questId, status) => {
        if (!isApiConfigured()) return
        const now = new Date().toISOString()
        const updates: Record<string, unknown> = { status }
        if (status === 'accepted') updates.accepted_at = now
        if (status === 'completed') updates.completed_at = now
        if (status === 'verified') updates.verified_at = now

        try {
          await api.patch(`/api/quests/${questId}`, updates)
          set((state) => ({
            partnerQuests: state.partnerQuests.map((q) =>
              q.id === questId
                ? {
                    ...q,
                    status,
                    acceptedAt: status === 'accepted' ? now : q.acceptedAt,
                    completedAt: status === 'completed' ? now : q.completedAt,
                    verifiedAt: status === 'verified' ? now : q.verifiedAt,
                  }
                : q
            ),
          }))

          const quest = get().partnerQuests.find((q) => q.id === questId)
          if (quest) {
            if (status === 'completed') {
              await get().addAffection(quest.partnershipId, quest.affectionReward || 10)
            } else if (status === 'verified') {
              await get().addAffection(quest.partnershipId, 5)
            }
          }
        } catch (error) {
          console.error('[Partnership] Failed to update quest:', error)
        }
      },

      completeQuestWithProof: async (questId, proofFile) => {
        if (!isApiConfigured()) return { success: false, error: 'API not configured' }

        const quest = get().partnerQuests.find((q) => q.id === questId)
        if (!quest) return { success: false, error: 'Quest not found' }

        const maxSize = 50 * 1024 * 1024
        if (proofFile.size > maxSize) return { success: false, error: 'File too large (max 50MB)' }

        try {
          // Get presigned upload URL
          const { uploadUrl, key } = await api.post<{ uploadUrl: string; key: string }>(
            '/api/media/upload-url',
            { contentType: proofFile.type }
          )

          // Upload directly to S3
          await fetch(uploadUrl, {
            method: 'PUT',
            body: proofFile,
            headers: { 'Content-Type': proofFile.type },
          })

          // Update quest
          const now = new Date().toISOString()
          await api.patch(`/api/quests/${questId}`, {
            status: 'completed',
            completed_at: now,
            proof_media_id: key,
          })

          set((state) => ({
            partnerQuests: state.partnerQuests.map((q) =>
              q.id === questId
                ? { ...q, status: 'completed' as const, completedAt: now, proofMediaId: key }
                : q
            ),
          }))

          await get().addAffection(quest.partnershipId, quest.affectionReward || 10)
          return { success: true }
        } catch (error) {
          console.error('[Partnership] Failed to complete quest with proof:', error)
          return { success: false, error: 'Failed to upload proof' }
        }
      },

      getQuestProofUrl: async (questId) => {
        if (!isApiConfigured()) return null
        const quest = get().partnerQuests.find((q) => q.id === questId)
        if (!quest?.proofMediaId) return null

        try {
          const { url } = await api.post<{ url: string }>('/api/storage/download-url', {
            bucket: 'private-media',
            key: quest.proofMediaId,
          })
          return url
        } catch {
          return null
        }
      },

      getQuestsAssignedToMe: () => {
        const userId = getCurrentUserId()
        return get().partnerQuests.filter(
          (q) => q.assignedTo === userId && !['verified', 'cancelled', 'expired'].includes(q.status)
        )
      },

      getQuestsAssignedByMe: () => {
        const userId = getCurrentUserId()
        return get().partnerQuests.filter(
          (q) => q.assignedBy === userId && !['cancelled', 'expired'].includes(q.status)
        )
      },

      getQuestWithDetails: async (questId) => {
        const quest = get().partnerQuests.find((q) => q.id === questId)
        if (!quest) return null

        const userId = getCurrentUserId()
        return {
          ...quest,
          assignerUsername: null,
          assignerDisplayName: null,
          assigneeUsername: null,
          assigneeDisplayName: null,
          isAssignedToMe: quest.assignedTo === userId,
          isAssignedByMe: quest.assignedBy === userId,
        }
      },

      subscribeToUpdates: () => {
        if (!isApiConfigured()) return () => {}

        const unsub1 = realtimeClient.on('partnership_updated', () => {
          get().fetchPartnerships()
        })
        const unsub2 = realtimeClient.on('quest_updated', () => {
          get().fetchPartnerQuests()
        })

        return () => {
          unsub1()
          unsub2()
        }
      },

      reset: () => {
        set({
          waifuMode: 'solo',
          partnerships: [],
          inviteCodes: [],
          partnerQuests: [],
          isLoading: false,
          error: null,
        })
      },
    }),
    {
      name: 'waifudo-partnerships',
      partialize: (state) => ({
        waifuMode: state.waifuMode,
        partnerships: state.partnerships,
        partnerQuests: state.partnerQuests,
      }),
    }
  )
)
